/**
 * Where a dropped row lands in a section's ordering.
 *
 * The editor keeps sparse sort orders (steps of 1000) and inserts by halving the gap, so a move
 * rewrites one row rather than renumbering the section. Pulled out of the drop handler because
 * the handler is 200 lines of branching and this is the part that is easy to get subtly wrong —
 * and, until CHG-2026-09-menu-editor-redesign, the part that silently did nothing when the two
 * sections differed.
 */

const STEP = 1000

/** Ascending sort orders of the rows already in the target section. */
export type Neighbours = Array<number>

/**
 * The sort order for a row inserted next to `targetIndex`, on the given edge.
 *
 * `neighbours` must not contain the moved row itself: when a row moves within its own section the
 * caller removes it first, otherwise it is its own neighbour and the midpoint is its current spot.
 */
export function sortOrderForDrop(
  neighbours: Neighbours,
  targetIndex: number,
  edge: 'top' | 'bottom',
): number {
  if (neighbours.length === 0) return STEP

  if (edge === 'top') {
    if (targetIndex <= 0) return Math.max(0, neighbours[0] - STEP)
    return midpoint(neighbours[targetIndex - 1], neighbours[targetIndex])
  }

  if (targetIndex >= neighbours.length - 1) {
    return neighbours[neighbours.length - 1] + STEP
  }
  return midpoint(neighbours[targetIndex], neighbours[targetIndex + 1])
}

/** The sort order for a row appended to the end of a section — an empty one included. */
export function sortOrderForAppend(neighbours: Neighbours): number {
  if (neighbours.length === 0) return STEP
  return Math.max(...neighbours) + STEP
}

/**
 * Halving the gap runs out when two neighbours are adjacent integers: floor() then returns the
 * lower one, which would place the row on the wrong side of it. Stepping past the lower neighbour
 * is wrong too, so the caller gets the lower value and the row lands where the drop indicator
 * promised — the ordering between those two stays as it was until something renumbers the
 * section. Rare enough to accept: it takes ten drops into the same gap.
 */
function midpoint(lower: number, upper: number): number {
  if (upper - lower <= 1) return lower
  return Math.floor((lower + upper) / 2)
}
